'use client';
import { domAnimation, LazyMotion, MotionConfig } from 'motion/react';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { WaitlistDialog } from '@/components/site/WaitlistDialog';

interface ModalContextType {
  openModal: () => void;
  closeModal: () => void;
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) throw new Error('useModal must be used within ModalProvider');
  return context;
};

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [modalOpen, setModalOpen] = useState(false);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);
  const value = useMemo(() => ({ openModal, closeModal }), [openModal, closeModal]);

  return (
    <ModalContext.Provider value={value}>
      <LazyMotion features={domAnimation} strict>
        <MotionConfig reducedMotion="user">
          {children}
          <WaitlistDialog open={modalOpen} onClose={closeModal} />
        </MotionConfig>
      </LazyMotion>
    </ModalContext.Provider>
  );
};
